// Everyone's notification bell, for the Executives (Pat, Oct 7 2026: "a notifications tile in the
// Executive tab so I can see everyone's notification bells to make sure they are valid"). Filter by
// person, kind, unread, words and how far back; remove one that should not have been sent — it goes
// from that person's bell (GET / DELETE /exec/notifications).
import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

const when = iso => { const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };
const dayOf = iso => { const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }); };
const typeLabel = t => String(t || 'other').replace(/_/g, ' ');
const ROLE = { Accounting: 'Office', OfficeStaff: 'Office', ProjectManager: 'PM', FieldSupervisor: 'Supervisor', ShopWorker: 'Shop' };
const sel = { border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 9px', fontSize: 13, background: '#fff', color: '#111827', maxWidth: 260 };

export default function ExecNotifications() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [user, setUser] = useState('');
  const [type, setType] = useState('');
  const [unread, setUnread] = useState(false);
  const [days, setDays] = useState(14);
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');
  useEffect(() => { const t = setTimeout(() => setQLive(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  const load = useCallback(() => {
    const p = new URLSearchParams({ days: String(days) });
    if (user) p.set('user', user); if (type) p.set('type', type); if (unread) p.set('unread', '1'); if (qLive) p.set('q', qLive);
    apiFetch(`/exec/notifications?${p}`).then(d => { setData(d); setErr(''); }).catch(e => setErr(e?.error || 'Could not load the notifications.'));
  }, [user, type, unread, days, qLive]);
  useEffect(() => { load(); }, [load]);

  async function remove(n) {
    if (!window.confirm(`Take this off ${n.name}'s bell?\n\n“${n.title}”\n\nIt is removed for good.`)) return;
    try { await apiFetch(`/exec/notifications/${n.id}`, { method: 'DELETE' }); setData(d => ({ ...d, items: d.items.filter(x => x.id !== n.id) })); }
    catch (e) { window.alert(e?.error || 'Could not remove it.'); }
  }

  if (err) return <div className="admin-card" style={{ padding: 20, color: '#b91c1c', fontWeight: 700 }}>{err}</div>;
  if (!data) return <div className="admin-card" style={{ padding: 20, color: '#9ca3af' }}>Reading everyone’s bells…</div>;
  const groups = [];
  data.items.forEach(n => { const d = dayOf(n.created_at); const g = groups[groups.length - 1]; if (g && g.d === d) g.rows.push(n); else groups.push({ d, rows: [n] }); });
  const totalUnread = data.people.reduce((s, p) => s + (p.unread || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="admin-card" style={{ padding: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={user} onChange={e => setUser(e.target.value)} style={sel}>
          <option value="">Everyone ({data.people.reduce((s, p) => s + p.n, 0)} · {totalUnread} unread)</option>
          {data.people.map(p => <option key={p.user_id} value={p.user_id}>{p.name}{p.role ? ` · ${ROLE[p.role] || p.role}` : ''} — {p.n}{p.unread ? ` (${p.unread} unread)` : ''}</option>)}
        </select>
        <select value={type} onChange={e => setType(e.target.value)} style={sel}>
          <option value="">Every kind</option>
          {data.types.map(t => <option key={t.type} value={t.type}>{typeLabel(t.type)} — {t.n}</option>)}
        </select>
        <select value={days} onChange={e => setDays(Number(e.target.value))} style={sel}>
          {[1, 7, 14, 30, 90].map(n => <option key={n} value={n}>Last {n === 1 ? 'day' : `${n} days`}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 700, color: '#374151' }}>
          <input type="checkbox" checked={unread} onChange={e => setUnread(e.target.checked)} /> Unread only
        </label>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search the words…" style={{ ...sel, flex: '1 1 180px', maxWidth: 320 }} />
        {(user || type || unread || q) && <button onClick={() => { setUser(''); setType(''); setUnread(false); setQ(''); }} style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Clear</button>}
      </div>
      <div style={{ fontSize: 13, color: '#6b7280' }}>{data.items.length}{data.items.length === 600 ? '+' : ''} notification{data.items.length === 1 ? '' : 's'}{user ? '' : ' across everyone'} in the last {data.days === 1 ? 'day' : `${data.days} days`}. Newest first.</div>
      {!data.items.length && <div className="admin-card" style={{ padding: 22, textAlign: 'center', color: '#9ca3af' }}>Nothing matches.</div>}
      {groups.map(g => (
        <div key={g.d}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '4px 0 6px' }}>{g.d}</div>
          <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
            {g.rows.map((n, i) => (
              <div key={n.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 14px', borderTop: i ? '1px solid #f1f1f1' : 'none', background: n.read ? '#fff' : '#f8fafc' }}>
                <span title={n.read ? 'Read' : 'Not read yet'} style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 6, flexShrink: 0, background: n.read ? '#e5e7eb' : '#2563eb' }} />
                <div style={{ width: 170, flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{n.name}</div>
                  <div style={{ fontSize: 11.5, color: '#6b7280' }}>{n.role ? (ROLE[n.role] || n.role) : ''}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827' }}>{n.title}</div>
                  {n.body && <div style={{ fontSize: 12.5, color: '#4b5563', marginTop: 2, whiteSpace: 'pre-wrap' }}>{n.body}</div>}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, fontSize: 11.5, color: '#9ca3af' }}>
                    <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 6, padding: '1px 7px', fontWeight: 700 }}>{typeLabel(n.type)}</span>
                    <span>{when(n.created_at)}</span>
                  </div>
                </div>
                <button onClick={() => remove(n)} title="Take this off their bell" style={{ background: 'none', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 7, padding: '4px 9px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>🗑 Remove</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
